// =====================================================
// Keyboard Shortcuts Hook
// Global keyboard shortcuts for review queue
// =====================================================

import { useEffect } from 'react';

export interface KeyboardShortcutHandlers {
  onNext?: () => void;
  onPrevious?: () => void;
  onApprove?: () => void;
  onEdit?: () => void;
  onReject?: () => void;
  onUploadReceipt?: () => void;
  onChangeVAT?: () => void;
  onSave?: () => void;
}

/**
 * Hook for registering keyboard shortcuts
 * Prevents triggering when typing in input fields
 */
export function useKeyboardShortcuts(handlers: KeyboardShortcutHandlers, enabled: boolean = true) {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      // Check for Cmd/Ctrl modifier
      const isModifierPressed = e.metaKey || e.ctrlKey;

      // Prevent default for shortcuts that conflict with browser
      const shouldPreventDefault = ['ArrowLeft', 'ArrowRight', 's', 'S'].includes(e.key) || isModifierPressed;

      switch (e.key) {
        case 'ArrowRight':
          if (shouldPreventDefault) e.preventDefault();
          handlers.onNext?.();
          break;
        case 'ArrowLeft':
          if (shouldPreventDefault) e.preventDefault();
          handlers.onPrevious?.();
          break;
        case 'a':
        case 'A':
          if (!isModifierPressed) {
            e.preventDefault();
            handlers.onApprove?.();
          }
          break;
        case 'e':
        case 'E':
          if (!isModifierPressed) {
            e.preventDefault();
            handlers.onEdit?.();
          }
          break;
        case 'r':
        case 'R':
          if (!isModifierPressed) {
            e.preventDefault();
            handlers.onReject?.();
          }
          break;
        case 'u':
        case 'U':
          if (!isModifierPressed) {
            e.preventDefault();
            handlers.onUploadReceipt?.();
          }
          break;
        case 'v':
        case 'V':
          if (!isModifierPressed) {
            e.preventDefault();
            handlers.onChangeVAT?.();
          }
          break;
        case 's':
        case 'S':
          if (isModifierPressed) {
            e.preventDefault();
            handlers.onSave?.();
          }
          break;
        case 'Escape':
          e.preventDefault();
          // ESC typically handled by modal/dialog components
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handlers, enabled]);
}
